import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
/* eslint-disable-next-line import/no-duplicates */
import {addDays, differenceInCalendarDays, isValid, parse} from 'date-fns';
import {MAX_FUTURE_DATE_DAYS, MAX_DATE_RANGE_DAYS} from 'src/lib/dates/constants';
/* eslint-disable-next-line import/no-duplicates */
import {enGB as en, fi, sv} from 'date-fns/locale';
import {offset, shift} from '@floating-ui/dom';
import DatePicker from 'react-datepicker';
import {SearchIcon} from '../../icons/SearchIcon';
import {fetchSearch, fetchSearchWithFilters} from '../../../services/api/api';
import {
    CaregiverObject,
    ClinicObjectSlim,
    PlacesFilterObject,
    ReasonFilterObject,
    Label,
    SearchRequest,
    SearchResult,
    Location
} from '../../../services/api/types/types';
import {XIcon} from '../../icons/XIcon';
import {PlaceFilterMap, SearchFilterMap} from '../../../hooks/useSearchFilters';
import {useMatchMedia} from '../../../hooks/useMatchMedia';
import {Pills} from '../../main/pills/pills';
import {SearchResults} from './SearchResults';
import {SearchHeader} from 'src/components/main/components/SearchHeader';
import {format} from 'src/lib/dates/format';
import './Search.scss';
import 'react-datepicker/dist/react-datepicker.css';
import {getLocale, getTextFromLocale} from 'src/services/i18n/locale';

export type Status = 'initial' | 'loading' | 'done' | 'error';

const supportedTimeOfDayValues = new Set(['ALLDAY', 'MORNING', 'AFTERNOON', 'DAYTIME', 'EVENING']);

export interface GroupedResults {
    visitReasons: VisitReasonGroup;
    places: PlaceGroup;
    careGivers: Group;
    clinics: ClinicGroup;
    locations: Group;
}

interface VisitReasonGroup {
    label: string;
    items: Array<VisitReasonResultItem>;
}
interface ClinicGroup {
    label: string;
    items: Array<ResultItem>;
}

interface Group {
    label: string;
    items: Array<ResultItem>;
}

interface PlaceGroup {
    label: string;
    items: Array<PlaceResultItem>;
}

interface ResultItem {
    id: string;
    type: keyof SearchFilterMap;
    label: string;
}
interface VisitReasonResultItem {
    id: string;
    type: keyof SearchFilterMap;
    label: Label[];
}

interface PlaceResultItem extends ResultItem {
    place: PlacesFilterObject;
}

interface SearchFiltersProps {
    reasonFilters: Array<ReasonFilterObject>;
    careGiverFilters: Array<CaregiverObject>;
    clinicFilters: Array<ClinicObjectSlim>;
    locationFilters: Array<Location>;
    placeFilters: PlaceFilterMap;
    searchParams: SearchFilterMap;
    includeDropIn: boolean;
    onSearchIdsChange(ids: Array<SearchResult>, isActive: boolean, appliedTypes?: Array<SearchResult['type']>): void;
    onClick(): void;
    onFocusUpdate(value: boolean): void;
    onResultsExpanded(value: boolean): void;
    onToggleFilter(type: keyof SearchFilterMap, id: string, active: boolean): void;
    onAddPlaceFilter(id: string, place: PlacesFilterObject): void;
    onRemovePlaceFilter(id: string): void;
    disableTabNavigation?: boolean;
}

export function Search({
    reasonFilters,
    careGiverFilters,
    placeFilters,
    clinicFilters,
    locationFilters,
    searchParams,
    includeDropIn,
    onSearchIdsChange,
    disableTabNavigation = false,
    onClick,
    onFocusUpdate,
    onResultsExpanded,
    onToggleFilter,
    onAddPlaceFilter,
    onRemovePlaceFilter
}: SearchFiltersProps) {
    const {t} = useTranslation();
    const useNativeDate = useMatchMedia('screen and (max-width: 600px)');
    const [urlSearchParams, setUrlSearchParams] = useSearchParams();
    const locale = getLocale();
    const locales = useMemo(() => ({en, fi, sv}), []);
    const datePickerPopperModifiers = useMemo(() => [offset(8), shift({padding: 12})], []);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const today = format(new Date(), 'yyyy-MM-dd');
    const defaultDateTo = useMemo(() => format(addDays(new Date(), MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd'), []);
    const maxFutureDate = useMemo(() => format(addDays(new Date(), MAX_FUTURE_DATE_DAYS), 'yyyy-MM-dd'), []);
    const maxDate = useMemo(() => parse(maxFutureDate, 'yyyy-MM-dd', new Date()), [maxFutureDate]);
    const minDate = useMemo(() => parse(today, 'yyyy-MM-dd', new Date()), [today]);
    const parseDateString = useCallback((value: string) => {
        const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
        return isValid(parsedDate) ? parsedDate : null;
    }, []);
    const clampDateParam = useCallback(
        (value: string | null) => {
            if (!value) {
                return null;
            }

            const parsedDate = parseDateString(value);
            if (!parsedDate) {
                return null;
            }

            const normalizedDate = format(parsedDate, 'yyyy-MM-dd');

            if (normalizedDate < today) {
                return today;
            }

            if (normalizedDate > maxFutureDate) {
                return maxFutureDate;
            }

            return normalizedDate;
        },
        [maxFutureDate, parseDateString, today]
    );
    const getSanitizedDateRange = useCallback(
        (fromParam: string | null, toParam: string | null) => {
            let nextDateFrom = clampDateParam(fromParam) || '';
            let nextDateTo = clampDateParam(toParam) || '';

            if (!nextDateFrom && !nextDateTo) {
                return {from: '', to: ''};
            }

            if (!nextDateFrom) {
                nextDateFrom = today;
            }

            const parsedDateFrom = parseDateString(nextDateFrom);

            if (!parsedDateFrom) {
                return {from: '', to: ''};
            }

            if (!nextDateTo) {
                nextDateTo = format(addDays(parsedDateFrom, MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');

                return {
                    from: nextDateFrom,
                    to: nextDateTo > maxFutureDate ? maxFutureDate : nextDateTo
                };
            }

            const parsedDateTo = parseDateString(nextDateTo);

            if (!parsedDateTo) {
                return {from: '', to: ''};
            }

            const interval = differenceInCalendarDays(parsedDateTo, parsedDateFrom);

            if (interval < 0 || interval > MAX_DATE_RANGE_DAYS) {
                nextDateTo = format(addDays(parsedDateFrom, MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');

                if (nextDateTo > maxFutureDate) {
                    nextDateTo = maxFutureDate;
                }
            }

            return {
                from: nextDateFrom,
                to: nextDateTo
            };
        },
        [clampDateParam, maxFutureDate, parseDateString, today]
    );
    const getUrlTimeOfDayValue = useCallback(() => {
        const paramValue = urlSearchParams.get('timeOfDay')?.toUpperCase();

        if (!paramValue || !supportedTimeOfDayValues.has(paramValue)) {
            return 'ALLDAY';
        }

        return paramValue;
    }, [urlSearchParams]);
    const [dateFrom, setDateFrom] = useState(() => {
        return getSanitizedDateRange(urlSearchParams.get('from'), urlSearchParams.get('to')).from;
    });
    const [dateTo, setDateTo] = useState(() => {
        return getSanitizedDateRange(urlSearchParams.get('from'), urlSearchParams.get('to')).to;
    });
    const [timeOfDay, setTimeOfDay] = useState(() => getUrlTimeOfDayValue());
    const [timeOfDayOpen, setTimeOfDayOpen] = useState(false);
    const [showIntervalLimitedNotice, setShowIntervalLimitedNotice] = useState(false);

    const updateDateTimeSearchParams = useCallback(
        (nextDateFrom: string, nextDateTo: string, nextTimeOfDay: string) => {
            const nextSearchParams = new URLSearchParams(urlSearchParams);

            if (nextDateFrom) {
                nextSearchParams.set('from', nextDateFrom);
            } else {
                nextSearchParams.delete('from');
            }
            if (nextDateTo) {
                nextSearchParams.set('to', nextDateTo);
            } else {
                nextSearchParams.delete('to');
            }
            nextSearchParams.set('timeOfDay', nextTimeOfDay);

            if (nextSearchParams.toString() === urlSearchParams.toString()) {
                return;
            }

            setUrlSearchParams(nextSearchParams, {replace: true});
        },
        [setUrlSearchParams, urlSearchParams]
    );

    useEffect(() => {
        const urlDateFrom = urlSearchParams.get('from');
        const urlDateTo = urlSearchParams.get('to');
        const {from: nextDateFrom, to: nextDateTo} = getSanitizedDateRange(urlDateFrom, urlDateTo);
        const nextTimeOfDay = getUrlTimeOfDayValue();
        const nextSearchParams = new URLSearchParams(urlSearchParams);
        let hasUrlChanges = false;

        if ((urlDateFrom || '') !== nextDateFrom) {
            if (nextDateFrom) {
                nextSearchParams.set('from', nextDateFrom);
            } else {
                nextSearchParams.delete('from');
            }
            hasUrlChanges = true;
        }

        if ((urlDateTo || '') !== nextDateTo) {
            if (nextDateTo) {
                nextSearchParams.set('to', nextDateTo);
            } else {
                nextSearchParams.delete('to');
            }
            hasUrlChanges = true;
        }

        const rawTimeOfDay = urlSearchParams.get('timeOfDay');
        if (rawTimeOfDay && rawTimeOfDay.toUpperCase() !== nextTimeOfDay) {
            nextSearchParams.set('timeOfDay', nextTimeOfDay);
            hasUrlChanges = true;
        }

        if (hasUrlChanges && nextSearchParams.toString() !== urlSearchParams.toString()) {
            setUrlSearchParams(nextSearchParams, {replace: true});
        }

        setDateFrom((currentDateFrom) => (currentDateFrom === nextDateFrom ? currentDateFrom : nextDateFrom));
        setDateTo((currentDateTo) => (currentDateTo === nextDateTo ? currentDateTo : nextDateTo));
        setTimeOfDay((currentTimeOfDay) => (currentTimeOfDay === nextTimeOfDay ? currentTimeOfDay : nextTimeOfDay));
    }, [getSanitizedDateRange, getUrlTimeOfDayValue, setUrlSearchParams, urlSearchParams]);

    const handleDateFromChange = useCallback(
        (date: Date | null) => {
            if (!date) {
                return;
            }

            const nextDateFrom = format(date, 'yyyy-MM-dd');
            let nextDateTo = dateTo;

            if (!nextDateTo) {
                nextDateTo = format(addDays(date, MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
            } else {
                const parsedDateTo = parseDateString(nextDateTo);

                if (!parsedDateTo) {
                    setShowIntervalLimitedNotice(true);
                    nextDateTo = format(addDays(date, MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
                } else {
                    const isInverted = differenceInCalendarDays(parsedDateTo, date) < 0;
                    const interval = Math.abs(differenceInCalendarDays(parsedDateTo, date));

                    if (isInverted || interval > MAX_DATE_RANGE_DAYS) {
                        setShowIntervalLimitedNotice(true);
                        nextDateTo = format(addDays(date, MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
                    }
                }
            }

            if (nextDateTo > maxFutureDate) {
                nextDateTo = maxFutureDate;
            }

            setDateFrom(nextDateFrom);
            setDateTo(nextDateTo);
            updateDateTimeSearchParams(nextDateFrom, nextDateTo, timeOfDay);
        },
        [dateTo, maxFutureDate, parseDateString, timeOfDay, updateDateTimeSearchParams]
    );

    const handleDateToChange = useCallback(
        (date: Date | null) => {
            if (!date) {
                return;
            }
            const nextDateTo = format(date, 'yyyy-MM-dd');
            let nextDateFrom = dateFrom || today;
            const parsedDateFrom = parseDateString(nextDateFrom);

            if (!parsedDateFrom) {
                setShowIntervalLimitedNotice(true);
                nextDateFrom = format(addDays(date, -MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
            } else {
                const isInverted = differenceInCalendarDays(date, parsedDateFrom) < 0;
                const interval = Math.abs(differenceInCalendarDays(date, parsedDateFrom));

                if (isInverted || interval > MAX_DATE_RANGE_DAYS) {
                    setShowIntervalLimitedNotice(true);
                    nextDateFrom = format(addDays(date, -MAX_DATE_RANGE_DAYS), 'yyyy-MM-dd');
                }
            }

            setDateTo(nextDateTo);
            setDateFrom(nextDateFrom);
            updateDateTimeSearchParams(nextDateFrom, nextDateTo, timeOfDay);
        },
        [dateFrom, parseDateString, timeOfDay, today, updateDateTimeSearchParams]
    );

    const handleTimeOfDayChange = useCallback(
        (nextTimeOfDay: string) => {
            setTimeOfDay(nextTimeOfDay);
            setTimeOfDayOpen(false);
            updateDateTimeSearchParams(dateFrom, dateTo, nextTimeOfDay);
        },
        [dateFrom, dateTo, updateDateTimeSearchParams]
    );

    const emptyGroupedResults = useCallback(() => {
        return {
            visitReasons: {
                label: t('search_group_visit_reason.text'),
                items: []
            },
            places: {
                label: t('search_group_place.text'),
                items: []
            },
            careGivers: {
                label: t('search_group_caregiver.text'),
                items: []
            },
            clinics: {
                label: t('search_group_clinic.text'),
                items: []
            },
            locations: {
                label: t('search_group_location.text'),
                items: []
            }
        } as GroupedResults;
    }, [t]);

    const [groupedResults, setGroupedResults] = useState<GroupedResults>(emptyGroupedResults());
    const [query, setQuery] = useState('');
    const [focused, _setFocused] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const focusRef = useRef(focused);
    const setFocused = (value: boolean) => {
        focusRef.current = value;
        _setFocused(value);
    };

    const [status, setStatus] = useState<Status>('initial');
    const [hasPlaceLoadError, setHasPlaceLoadError] = useState<boolean>(false);
    const onSearchIdsChangeRef = useRef(onSearchIdsChange);

    const reasonIdsKey = searchParams.reason.join(',');
    const clinicIdsKey = searchParams.clinic.join(',');
    const caregiverIdsKey = searchParams.caregiver.join(',');
    const visitReasonIds = reasonIdsKey ? reasonIdsKey.split(',').map((id) => parseInt(id, 10)) : [];
    const clinicIds = clinicIdsKey ? clinicIdsKey.split(',').map((id) => parseInt(id, 10)) : [];
    const careGiverIds = caregiverIdsKey ? caregiverIdsKey.split(',').map((id) => parseInt(id, 10)) : [];

    const hasQuery = useCallback(() => query.length > 2, [query]);

    const queryResults = useCallback(
        async (abortSignal: AbortSignal) => {
            if (query.length < 2) {
                return;
            }
            setHasPlaceLoadError(false);
            setGroupedResults(emptyGroupedResults());

            let results = emptyGroupedResults();

            results.visitReasons.items = reasonFilters
                .filter(({name}) => getTextFromLocale(name).toLowerCase().includes(query.toLowerCase()))
                .slice(0, 12)
                .map(({id, name}) => {
                    return {
                        id,
                        label: name,
                        type: 'reason'
                    } satisfies VisitReasonResultItem;
                });

            results.careGivers.items = careGiverFilters
                .filter(({label}) => label.toLowerCase().includes(query.toLowerCase()))
                .slice(0, 12)
                .map(({id, label}) => {
                    return {
                        id: id.toString(),
                        label,
                        type: 'caregiver'
                    } satisfies ResultItem;
                });

            results.clinics.items = clinicFilters
                .filter(({name}) => name.toLowerCase().includes(query.toLowerCase()))
                .slice(0, 12)
                .map(({id, name}) => {
                    return {
                        id,
                        label: name,
                        type: 'clinic'
                    } satisfies ResultItem;
                });

            results.locations.items = locationFilters
                .filter(({name}) => name.toLowerCase().includes(query.toLowerCase()))
                .slice(0, 12)
                .map(({id, name}) => {
                    return {
                        id: id.toString(),
                        label: name,
                        type: 'location'
                    } satisfies ResultItem;
                });

            try {
                const placeResults = await fetchSearch(query, abortSignal);

                results.places.items = placeResults.map((place) => {
                    return {
                        id: place.id,
                        label: place.name,
                        type: 'place',
                        place
                    } satisfies PlaceResultItem;
                });

                const visitReasonsSlotsOpen =
                    results.visitReasons.items.length > 3 ? 0 : 3 - results.visitReasons.items.length;

                const careGiversSlotsOpen =
                    results.careGivers.items.length > 3 ? 0 : 3 - results.careGivers.items.length;

                const placesSlotsOpen = results.places.items.length > 3 ? 0 : 3 - results.places.items.length;
                const clinicsSlotsOpen = results.clinics.items.length > 3 ? 0 : 3 - results.clinics.items.length;
                const slotsOpen = visitReasonsSlotsOpen + careGiversSlotsOpen + placesSlotsOpen + clinicsSlotsOpen;

                let normalizedResults = emptyGroupedResults();
                normalizedResults.visitReasons.items.push(...results.visitReasons.items.slice(0, 3));
                normalizedResults.careGivers.items.push(...results.careGivers.items.slice(0, 3));
                normalizedResults.places.items.push(...results.places.items.slice(0, 3));
                normalizedResults.clinics.items.push(...results.clinics.items.slice(0, 3));
                normalizedResults.locations.items.push(...results.locations.items.slice(0, 3));
                if (slotsOpen > 0) {
                    let remainingSlots = slotsOpen;
                    for (let i = 0; i < slotsOpen; i++) {
                        if (visitReasonsSlotsOpen === 0 && remainingSlots > 0) {
                            const nextVisitReason = results.visitReasons.items[3 + i];
                            !!nextVisitReason &&
                                normalizedResults.visitReasons.items.push(nextVisitReason) &&
                                remainingSlots--;
                        }
                        if (careGiversSlotsOpen === 0 && remainingSlots > 0) {
                            const nextCareGiver = results.careGivers.items[3 + i];
                            !!nextCareGiver &&
                                normalizedResults.careGivers.items.push(nextCareGiver) &&
                                remainingSlots--;
                        }
                        if (placesSlotsOpen === 0 && remainingSlots > 0) {
                            const nextPlace = results.places.items[3 + i];
                            !!nextPlace && normalizedResults.places.items.push(nextPlace) && remainingSlots--;
                        }
                        if (clinicsSlotsOpen === 0 && remainingSlots > 0) {
                            const nextClinic = results.clinics.items[3 + i];
                            !!nextClinic && normalizedResults.clinics.items.push(nextClinic) && remainingSlots--;
                        }
                    }
                }
                results = normalizedResults;
            } catch (error) {
                const e = error as Error;
                if (e.name === 'AbortError') {
                    setStatus('loading');
                    return;
                }
                setHasPlaceLoadError(true);
            }

            setGroupedResults(results);
            setStatus('done');
        },
        [query, emptyGroupedResults, reasonFilters, careGiverFilters, clinicFilters, locationFilters]
    );

    const clearQuery = () => {
        setQuery('');
        setFocused(false);
    };

    useEffect(() => {
        onSearchIdsChangeRef.current = onSearchIdsChange;
    }, [onSearchIdsChange]);

    useEffect(() => {
        setStatus('loading');
        const abortSignal = new AbortController();

        const debouncedQuery = setTimeout(() => {
            queryResults(abortSignal.signal);
        }, 250);

        return () => {
            abortSignal.abort();
            clearTimeout(debouncedQuery);
        };
    }, [queryResults, hasQuery]);

    useEffect(() => {
        if (isExpanded === hasQuery()) {
            return;
        }

        setIsExpanded(hasQuery());
        onResultsExpanded(hasQuery());
    }, [status, hasQuery, onResultsExpanded, isExpanded]);

    useEffect(() => {
        if (!showIntervalLimitedNotice) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setShowIntervalLimitedNotice(false);
        }, 7000);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [showIntervalLimitedNotice]);

    // Used to hide native keyboard on mobile when touch on a result
    const blurSearchFocus = useCallback(() => {
        if (searchInputRef.current) {
            setFocused(false);
            searchInputRef.current.blur();
        }
    }, [searchInputRef]);

    // Call search API when relevant filter values change.
    useEffect(() => {
        // With no selected dates, ALLDAY searches only request location results.
        const isDatePlaceholderState = !dateFrom && !dateTo;
        const appliedTypes: Array<SearchResult['type']> =
            isDatePlaceholderState && timeOfDay === 'ALLDAY' ? ['LOCATION'] : ['CLINIC', 'LOCATION'];

        const searchRequest: SearchRequest = {
            from: dateFrom || today,
            to: dateTo || defaultDateTo,
            timeOfDay,
            includeDropIn
        };

        if (isDatePlaceholderState) {
            searchRequest.types = appliedTypes;
        }

        if (visitReasonIds.length > 0) {
            searchRequest.visitReasonIds = visitReasonIds;
        }

        let active = true;
        const searches = [fetchSearchWithFilters(searchRequest)];
        if (visitReasonIds.length > 1) {
            searches.push(
                ...visitReasonIds.map((id) => fetchSearchWithFilters({...searchRequest, visitReasonIds: [id]}))
            );
        }

        Promise.all(searches)
            .then(([results, ...reasonResults]) => {
                if (!active) {
                    return;
                }

                if (reasonResults.length > 0) {
                    results = results.filter(({id, type}) => {
                        if (type === 'LOCATION') {
                            return reasonResults.every((matches) =>
                                matches.some((match) => match.type === 'LOCATION' && match.id === id)
                            );
                        }
                        return true;
                    });
                }
                onSearchIdsChangeRef.current(results, true, appliedTypes);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                onSearchIdsChangeRef.current([], false);
                console.error('Search with filters failed:', error);
            });

        return () => {
            active = false;
        };
    }, [dateFrom, dateTo, defaultDateTo, today, timeOfDay, includeDropIn, reasonIdsKey]);

    return (
        <>
            <section className='search-header-container'>
                <SearchHeader toggleSearchViewClick={onClick} />
            </section>
            <form className={`search_box_form ${focused ? 'search_box_form--focused' : ''}`} role='search'>
                <div className='search_box__input-container'>
                    <label className={`search_box ${focused ? ' has-focus' : ''}`}>
                        <SearchIcon />
                        <input
                            ref={searchInputRef}
                            autoComplete='off'
                            tabIndex={disableTabNavigation ? -1 : 0}
                            onFocus={() => {
                                if (focusRef.current === false) {
                                    setFocused(true);
                                    onFocusUpdate(true);
                                }
                            }}
                            enterKeyHint='search'
                            onBlur={() => {
                                if (focusRef.current === true) {
                                    setFocused(false);
                                    onFocusUpdate(false);
                                }
                            }}
                            type='text'
                            id='search'
                            placeholder={t('search_input.placeholder')}
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    blurSearchFocus();
                                    e.preventDefault();
                                }
                            }}
                        />
                        {hasQuery() && (
                            <button
                                onFocus={() => {
                                    setFocused(true);
                                }}
                                className='svg-white search_box__clear-button'
                                aria-label={t('search_input.clear')}
                                onClick={() => {
                                    clearQuery();
                                }}
                            >
                                <XIcon />
                            </button>
                        )}
                    </label>
                </div>
                <div className='search_box__filters' aria-label='search filters'>
                    <div className='search_box__filters-row search_box__filters-row--dates'>
                        <label className='search_box__field' htmlFor='search-date-from'>
                            {useNativeDate ? (
                                <input
                                    id='search-date-from'
                                    type='date'
                                    autoComplete='off'
                                    aria-label={t('search_filters.date_from')}
                                    className='search_box__field-control--date'
                                    value={dateFrom}
                                    min={today}
                                    max={maxFutureDate}
                                    onChange={(event) => {
                                        const date = parseDateString(event.target.value);
                                        if (date) {
                                            handleDateFromChange(date);
                                        }
                                    }}
                                />
                            ) : (
                                <DatePicker
                                    id='search-date-from'
                                    aria-label={t('search_filters.date_from')}
                                    autoComplete='off'
                                    className='search_box__field-control--date'
                                    selected={parseDateString(dateFrom)}
                                    placeholderText={t('search_filters.date_from')}
                                    openToDate={parseDateString(dateFrom) || minDate}
                                    onChange={handleDateFromChange}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    locale={locales[locale]}
                                    showPopperArrow={false}
                                    popperPlacement='bottom-start'
                                    popperModifiers={datePickerPopperModifiers}
                                    popperClassName='search_box__datepicker-popper'
                                    portalId='search-date-picker-portal'
                                    dateFormat='yyyy-MM-dd'
                                />
                            )}
                        </label>
                        <label className='search_box__field' htmlFor='search-date-to'>
                            {useNativeDate ? (
                                <input
                                    id='search-date-to'
                                    type='date'
                                    autoComplete='off'
                                    aria-label={t('search_filters.date_to')}
                                    className='search_box__field-control--date'
                                    value={dateTo}
                                    min={dateFrom}
                                    max={maxFutureDate}
                                    onChange={(event) => {
                                        const date = parseDateString(event.target.value);
                                        if (date) {
                                            handleDateToChange(date);
                                        }
                                    }}
                                />
                            ) : (
                                <DatePicker
                                    id='search-date-to'
                                    aria-label={t('search_filters.date_to')}
                                    autoComplete='off'
                                    className='search_box__field-control--date'
                                    selected={parseDateString(dateTo)}
                                    placeholderText={t('search_filters.date_to')}
                                    openToDate={parseDateString(dateTo) || parseDateString(defaultDateTo) || minDate}
                                    onChange={handleDateToChange}
                                    minDate={parseDateString(dateFrom) || minDate}
                                    maxDate={maxDate}
                                    locale={locales[locale]}
                                    showPopperArrow={false}
                                    popperPlacement='bottom-start'
                                    popperModifiers={datePickerPopperModifiers}
                                    popperClassName='search_box__datepicker-popper'
                                    portalId='search-date-picker-portal'
                                    dateFormat='yyyy-MM-dd'
                                />
                            )}
                        </label>
                    </div>
                    {showIntervalLimitedNotice && (
                        <div className='search_box__interval-limited-notice' role='status' aria-live='polite'>
                            {t('search_filters.interval_limited_notice')}
                        </div>
                    )}
                    <span
                        className={`select-container search_box__select-container${timeOfDayOpen ? ' search_box__select-container--open' : ''}`}
                    >
                        <select
                            id='time-of-day'
                            value={timeOfDay}
                            className='select'
                            aria-label={t('search_filters.time_of_day')}
                            onMouseDown={() => setTimeOfDayOpen((prev) => !prev)}
                            onBlur={() => setTimeOfDayOpen(false)}
                            onChange={(event) => {
                                handleTimeOfDayChange(event.target.value);
                            }}
                        >
                            <option value='ALLDAY'>{t('search_filters.time_of_day_options.allday')}</option>
                            <option value='MORNING'>{t('search_filters.time_of_day_options.morning')}</option>
                            <option value='AFTERNOON'>{t('search_filters.time_of_day_options.afternoon')}</option>
                            <option value='EVENING'>{t('search_filters.time_of_day_options.evening')}</option>
                        </select>
                    </span>
                </div>
                {hasQuery() && (
                    <SearchResults
                        groupedResults={groupedResults}
                        searchParams={searchParams}
                        onToggleFilter={onToggleFilter}
                        clearQuery={clearQuery}
                        disableTabNavigation={disableTabNavigation}
                        onAddPlaceFilter={onAddPlaceFilter}
                        hasPlaceLoadError={hasPlaceLoadError}
                        status={status}
                    />
                )}
                {!hasQuery() &&
                    (searchParams.clinic.length > 0 ||
                        searchParams.caregiver.length > 0 ||
                        searchParams.place.length > 0 ||
                        searchParams.location.length > 0) && (
                        <Pills
                            places={searchParams.place.map((placeId) => {
                                return (
                                    placeFilters[placeId] || {
                                        id: placeId,
                                        name: t('search_item_unknown.text'),
                                        category: 'unknown'
                                    }
                                );
                            })}
                            clinics={searchParams.clinic.map((clinicId) => {
                                const clinic = clinicFilters.find(({id}) => id === clinicId);
                                return clinic
                                    ? {
                                          id: clinic.id,
                                          name: clinic.name,
                                          category: 'clinic'
                                      }
                                    : {
                                          id: clinicId,
                                          name: t('search_item_unknown.text'),
                                          category: 'unknown'
                                      };
                            })}
                            caregivers={
                                searchParams.caregiver.map((careGiverId) => {
                                    return (
                                        careGiverFilters.find(({id}) => id.toString() === careGiverId) || {
                                            id: careGiverId,
                                            label: t('search_item_unknown.text'),
                                            category: 'unknown'
                                        }
                                    );
                                }) as Array<CaregiverObject>
                            }
                            locations={
                                searchParams.location.map((locationId) => {
                                    return (
                                        locationFilters.find(({id}) => id.toString() === locationId) || {
                                            id: locationId,
                                            label: t('search_item_unknown.text'),
                                            category: 'unknown'
                                        }
                                    );
                                }) as Array<Location>
                            }
                            removePlace={(placeId) => onRemovePlaceFilter(placeId)}
                            removeCareGiver={(careGiverId) => onToggleFilter('caregiver', careGiverId, false)}
                            removeClinic={(clinicId) => onToggleFilter('clinic', clinicId, false)}
                            removeLocation={(locationId) => onToggleFilter('location', locationId, false)}
                        />
                    )}
            </form>
        </>
    );
}
